(function () {
  'use strict';

  window.BM = window.BM || {};
  if (window.BM.__customMountainProductLoaded) return;
  window.BM.__customMountainProductLoaded = true;

  const ROOT_SELECTOR = '.bm_customMountain_root[data-bm-section-id]';
  const MAP_SELECTION_EVENT = 'bm:peakExplorer:selection-change';
  const SELECTION_LOCK_EVENT = 'bm:peakExplorer:selection-lock-change';
  const SNOW_SETTINGS_EVENT = 'bm:peakExplorer:snow-settings-change';
  const GPX_SETTINGS_EVENT = 'bm:peakExplorer:gpx-change';
  const VARIANT_SYNC_DELAY_MS = 150;
  // Eine bewusste vertikale Wischbewegung; kleine Fingerbewegungen bleiben folgenlos.
  const EXAMPLE_DISMISS_SWIPE_PX = 80;
  const EXAMPLE_DISMISS_MAX_HORIZONTAL_PX = 60;

  function formatNumber(value, digits = 2) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '–';
    return number.toLocaleString('de-DE', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }

  function parseJsonElement(element, fallback) {
    if (!element?.textContent) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch {
      return fallback;
    }
  }

  function getVariantOptions(variant) {
    if (Array.isArray(variant?.options)) {
      return variant.options.map(value => String(value || '').trim()).filter(Boolean);
    }

    return [variant?.option1, variant?.option2, variant?.option3]
      .map(value => String(value || '').trim())
      .filter(Boolean);
  }

  function readSelectedOptions(root, variantInput, productData) {
    const variantId = String(variantInput?.value || '');
    const variants = Array.isArray(productData?.variants) ? productData.variants : [];
    const optionNames = Array.isArray(productData?.optionNames) ? productData.optionNames : [];
    const selectedVariant = variants.find(variant => String(variant?.id || '') === variantId);
    const variantOptions = getVariantOptions(selectedVariant);

    if (variantOptions.length) {
      if (variantOptions.length === 1 && variantOptions[0] === 'Default Title') {
        return 'Standardauswahl';
      }

      return variantOptions
        .map((value, index) => `${optionNames[index] || `Option ${index + 1}`}: ${value}`)
        .join(' · ');
    }

    const fallbackValues = [];
    root.querySelectorAll('variant-selects fieldset').forEach(fieldset => {
      const value = fieldset.querySelector('input[type="radio"]:checked')?.value?.trim();
      if (value) fallbackValues.push(value);
    });
    root.querySelectorAll('variant-selects select').forEach(select => {
      const value = select.selectedOptions?.[0]?.textContent?.trim();
      if (value) fallbackValues.push(value);
    });

    const uniqueValues = [...new Set(fallbackValues)];
    if (!uniqueValues.length) return 'Standardauswahl';

    return uniqueValues
      .map((value, index) => `${optionNames[index] || `Option ${index + 1}`}: ${value}`)
      .join(' · ');
  }

  function findLinkedLayoutPart(root, partName) {
    const localPart = root.querySelector(`[data-bm-layout-part="${partName}"]`);
    if (localPart) return localPart;

    const productId = String(root.dataset.productId || '');
    if (!productId) return null;

    return Array.from(document.querySelectorAll(`[data-bm-layout-part="${partName}"]`))
      .find(part => String(part.dataset.bmProductId || '') === productId) || null;
  }

  function readSelectionProductId(selectionRoot) {
    const linkedPart = selectionRoot.closest('[data-bm-product-id]');
    if (linkedPart?.dataset.bmProductId) return String(linkedPart.dataset.bmProductId);

    const productElement = selectionRoot.querySelector(
      'script[type="application/json"][data-product]'
    );
    const productData = parseJsonElement(productElement, null);
    return productData?.id == null ? '' : String(productData.id);
  }

  function findLinkedSelectionRoot(root) {
    const localSelectionRoot = root.querySelector('.bm-custom-peak');
    if (localSelectionRoot) return localSelectionRoot;

    const productId = String(root.dataset.productId || '');
    if (!productId) return null;

    const matches = Array.from(document.querySelectorAll('.bm-custom-peak'))
      .filter(selectionRoot => readSelectionProductId(selectionRoot) === productId);

    return matches.find(selectionRoot => !selectionRoot.hidden && !selectionRoot.closest('[hidden]'))
      || matches[0]
      || null;
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement) || root.dataset.bmInitialized === 'true') return;
    root.dataset.bmInitialized = 'true';

    const summaryScope = findLinkedLayoutPart(root, 'order-summary');
    const form = summaryScope?.querySelector('form[action*="/cart/add"]');
    const submitButton = summaryScope?.querySelector('[data-bm-role="submit"]');
    const confirmButton = summaryScope?.querySelector('[data-bm-role="confirm-selection"]');
    const confirmState = summaryScope?.querySelector('[data-bm-role="confirm-state"]');
    const errorBox = summaryScope?.querySelector('[data-bm-role="validation-error"]');
    const confirmationInput = form?.querySelector('[data-bm-role="confirmation-property"]');
    const snowPropertyInput = form?.querySelector('[data-bm-role="snow-property"]');
    const snowlinePropertyInput = form?.querySelector('[data-bm-role="snowline-property"]');
    const selectionRoot = findLinkedSelectionRoot(root);
    const selectionLockButton = selectionRoot?.querySelector('[data-role="toggle-selection-lock"]');
    const snowSlider = selectionRoot?.querySelector('[data-role="snow-slider"]');
    const summaryVariant = summaryScope?.querySelector('[data-bm-summary="variant"]');
    const summaryCenter = summaryScope?.querySelector('[data-bm-summary="center"]');
    const summarySize = summaryScope?.querySelector('[data-bm-summary="size"]');
    const summaryRotation = summaryScope?.querySelector('[data-bm-summary="rotation"]');
    const summaryQuantity = summaryScope?.querySelector('[data-bm-summary="quantity"]');
    const quantityInput = root.querySelector('[name="quantity"]');
    const purchasabilityElement = root.querySelector(
      'script[type="application/json"][data-bm-variant-purchasability]'
    );
    const productDataElement = selectionRoot?.querySelector(
      'script[type="application/json"][data-product]'
    );
    const dynamicCheckout = summaryScope?.querySelector('[data-bm-role="dynamic-checkout"]');
    const productOptionsCartEnabled = root.dataset.bmProductOptionsCartEnabled === 'true';
    const instructions = root.querySelector('.bm_customMountain_instructions');
    const instructionSteps = instructions
      ? Array.from(instructions.querySelectorAll('.bm_customMountain_instructionStep'))
      : [];
    const instructionMediaQuery = window.matchMedia('(min-width: 750px)');
    const variantExample = root.querySelector('[data-bm-role="variant-example"]');
    const variantExampleTrigger = variantExample?.querySelector('[data-bm-role="variant-example-open"]');
    const variantExampleImage = variantExample?.querySelector('[data-bm-role="variant-example-image"]');
    const variantExampleMissing = variantExample?.querySelector('[data-bm-role="variant-example-missing"]');
    const variantExampleLabel = variantExample?.querySelector('[data-bm-role="variant-example-label"]');
    const variantExampleDialog = variantExample?.querySelector('[data-bm-role="variant-example-dialog"]');
    const variantExampleDialogImage = variantExample?.querySelector('[data-bm-role="variant-example-dialog-image"]');
    const variantExampleDialogLabel = variantExample?.querySelector('[data-bm-role="variant-example-dialog-label"]');
    const variantExampleClose = variantExample?.querySelector('[data-bm-role="variant-example-close"]');
    const variantExampleEntries = new Map(
      Array.from(variantExample?.querySelectorAll('[data-variant-id]') || [])
        .map(entry => [String(entry.dataset.variantId || ''), entry])
        .filter(([variantId]) => variantId)
    );

    if (!form || !submitButton || !confirmButton || !selectionRoot || !selectionLockButton) {
      delete root.dataset.bmInitialized;
      return;
    }

    const keepMainMediaStable = root.dataset.bmKeepMainMediaStable === 'true';
    const hadOwnUpdateMedia = Object.prototype.hasOwnProperty.call(root, 'updateMedia');
    const originalUpdateMedia = typeof root.updateMedia === 'function' ? root.updateMedia : null;
    if (keepMainMediaStable && originalUpdateMedia) {
      root.updateMedia = function bm_customMountain_keepMainMediaStable() {};
    }

    const expectedProductFormId = String(root.dataset.bmProductFormId || '').trim();
    if (expectedProductFormId && form.id !== expectedProductFormId) {
      form.id = expectedProductFormId;
    }

    const productVariants = parseJsonElement(purchasabilityElement, []);
    const productData = parseJsonElement(productDataElement, { optionNames: [], variants: [] });
    let selection = null;
    let confirmed = false;
    let disposed = false;
    let enforcingButtonState = false;
    let variantFrameId = null;
    let variantTimeoutId = null;
    let instructionViewport = '';
    let exampleTouchStart = null;
    const initialSnowMode = String(selectionRoot?.dataset.bmSnowMode || 'off');
    let snowSettings = {
      available: Boolean(snowSlider),
      enabled: initialSnowMode !== 'off',
      baseSnowlineM: Number(snowSlider?.value)
    };
    let gpxUploadPending = false;

    function getVariantInput() {
      return form.querySelector('[name="id"]');
    }

    function applyResponsiveInstructionState() {
      if (!instructions || !instructionSteps.length) return;

      const nextViewport = instructionMediaQuery.matches ? 'desktop' : 'mobile';
      if (instructionViewport === nextViewport) return;

      const dataKey = nextViewport === 'desktop'
        ? 'bmInstructionsOpenDesktop'
        : 'bmInstructionsOpenMobile';
      const shouldOpen = instructions.dataset[dataKey] === 'true';
      instructionSteps.forEach(step => {
        step.open = shouldOpen;
      });
      instructionViewport = nextViewport;
    }

    function updateVariantExample() {
      if (!variantExample) return;

      const variantId = String(getVariantInput()?.value || findSelectedVariant()?.id || '');
      const entry = variantExampleEntries.get(variantId);
      const title = String(entry?.dataset.title || 'Ausgewählte Variante');
      const smallSource = String(entry?.dataset.srcSmall || '');
      const sourceSet = String(entry?.dataset.srcset || '');
      const largeSource = String(entry?.dataset.srcLarge || '');
      const alternativeText = String(entry?.dataset.alt || title);
      const imageWidth = Number(entry?.dataset.width) || 1;
      const imageHeight = Number(entry?.dataset.height) || 1;
      const hasImage = Boolean(smallSource && largeSource);

      if (variantExampleLabel) variantExampleLabel.textContent = title;
      if (variantExampleDialogLabel) variantExampleDialogLabel.textContent = title;
      if (variantExampleTrigger) variantExampleTrigger.hidden = !hasImage;
      if (variantExampleMissing) variantExampleMissing.hidden = hasImage;

      if (!hasImage) {
        if (variantExampleDialog?.open) variantExampleDialog.close();
        return;
      }

      if (variantExampleImage) {
        variantExampleImage.src = smallSource;
        variantExampleImage.srcset = sourceSet;
        variantExampleImage.alt = alternativeText;
      }
      if (variantExampleTrigger) {
        variantExampleTrigger.href = largeSource;
        variantExampleTrigger.setAttribute('aria-label', `${title} vergrößert anzeigen`);
      }
      if (variantExampleDialogImage) {
        variantExampleDialogImage.dataset.largeSrc = largeSource;
        if (variantExampleDialog?.open) variantExampleDialogImage.src = largeSource;
        variantExampleDialogImage.alt = alternativeText;
        variantExampleDialogImage.width = imageWidth;
        variantExampleDialogImage.height = imageHeight;
      }
    }

    function handleVariantExampleOpen(event) {
      if (!variantExampleDialog || typeof variantExampleDialog.showModal !== 'function') return;
      event.preventDefault();
      if (!variantExampleDialog.open) {
        if (variantExampleDialogImage?.dataset.largeSrc) {
          variantExampleDialogImage.src = variantExampleDialogImage.dataset.largeSrc;
        }
        variantExampleDialog.showModal();
        variantExampleClose?.focus({ preventScroll: true });
      }
    }

    function closeVariantExample() {
      exampleTouchStart = null;
      if (variantExampleDialog?.open) variantExampleDialog.close();
    }

    function handleVariantExampleBackdrop(event) {
      if (event.target !== variantExampleDialog) return;
      const bounds = variantExampleDialog.getBoundingClientRect();
      if (
        event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom
      ) closeVariantExample();
    }

    function handleVariantExampleTouchStart(event) {
      exampleTouchStart = event.touches.length === 1 && variantExampleDialog?.scrollTop === 0
        ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
        : null;
    }

    function handleVariantExampleTouchEnd(event) {
      const start = exampleTouchStart;
      exampleTouchStart = null;
      if (!start || event.touches.length || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      if (
        touch.clientY - start.y >= EXAMPLE_DISMISS_SWIPE_PX &&
        Math.abs(touch.clientX - start.x) <= EXAMPLE_DISMISS_MAX_HORIZONTAL_PX &&
        variantExampleDialog?.scrollTop === 0
      ) closeVariantExample();
    }

    function handleVariantExampleTouchCancel() {
      exampleTouchStart = null;
    }

    function getSelectedOptionValues() {
      const variantSelects = root.querySelector('variant-selects');
      if (!variantSelects) return [];

      return Array.from(
        variantSelects.querySelectorAll('fieldset, select[name^="options["]')
      ).map(group => {
        if (group.matches('select')) return String(group.value || '').trim();
        return String(
          group.querySelector('input[type="radio"]:checked')?.value || ''
        ).trim();
      });
    }

    function findSelectedVariant() {
      const variants = Array.isArray(productData?.variants) ? productData.variants : [];
      const selectedValues = getSelectedOptionValues();
      if (!variants.length || !selectedValues.length || selectedValues.some(value => !value)) {
        return null;
      }

      return variants.find(variant => {
        const variantOptions = getVariantOptions(variant);
        return selectedValues.every(
          (value, index) => String(variantOptions[index] || '').trim() === value
        );
      }) || null;
    }

    function syncVariantSelectionToForm() {
      const variant = findSelectedVariant();
      const variantInput = getVariantInput();
      if (!variant || !variantInput) return false;

      const nextVariantId = String(variant.id || '');
      if (!nextVariantId) return false;

      const availability = productVariants.find(
        item => String(item?.id || '') === nextVariantId
      );
      const isPurchasable = availability
        ? availability.purchasable === true
        : variant.available !== false;
      const hasChanged = String(variantInput.value || '') !== nextVariantId;

      variantInput.value = nextVariantId;
      variantInput.disabled = !isPurchasable;
      if (hasChanged) {
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return hasChanged;
    }

    function isCurrentVariantAvailable() {
      const variantInput = getVariantInput();
      const variantId = String(variantInput?.value || '');
      const variant = productVariants.find(item => String(item?.id || '') === variantId);
      return variant ? variant.purchasable === true : !variantInput?.disabled;
    }

    function isCoordinatesLocked() {
      const controller = selectionRoot.bm_peakExplorer_selectionLock;
      if (typeof controller?.isLocked === 'function') return controller.isLocked();
      return selectionLockButton?.getAttribute('aria-checked') === 'true';
    }

    function setCoordinatesLocked(nextLocked, announce = true) {
      const desiredState = Boolean(nextLocked);
      const controller = selectionRoot.bm_peakExplorer_selectionLock;

      if (typeof controller?.setLocked === 'function') {
        return controller.setLocked(desiredState, announce) === desiredState;
      }

      if (selectionLockButton && isCoordinatesLocked() !== desiredState) {
        selectionLockButton.click();
      }
      return isCoordinatesLocked() === desiredState;
    }

    function announceError(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = !message;
      if (message) {
        errorBox.setAttribute('tabindex', '-1');
        errorBox.focus({ preventScroll: true });
      }
    }

    function clearError() {
      if (!errorBox) return;
      errorBox.textContent = '';
      errorBox.hidden = true;
    }

    function setSubmitBlocked(blocked) {
      enforcingButtonState = true;
      if (blocked) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.dataset.bmBlockedByConfiguration = 'true';
      } else {
        delete submitButton.dataset.bmBlockedByConfiguration;
        if (isCurrentVariantAvailable()) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-disabled');
        } else {
          submitButton.disabled = true;
          submitButton.setAttribute('aria-disabled', 'true');
        }
      }
      enforcingButtonState = false;
    }

    function updateConfirmationState() {
      const coordinatesLocked = isCoordinatesLocked();
      confirmed = Boolean(confirmed && coordinatesLocked && selection);
      root.dataset.bmSelectionConfirmed = String(confirmed);
      confirmButton.disabled = !selection;
      confirmButton.setAttribute('aria-disabled', String(!selection));
      confirmButton.setAttribute('aria-pressed', String(confirmed));
      confirmButton.textContent = confirmed
        ? 'Kartenausschnitt ändern'
        : 'Kartenausschnitt bestätigen';

      if (confirmationInput) {
        confirmationInput.value = confirmed ? 'Ja' : 'Nein';
      }

      if (dynamicCheckout) {
        dynamicCheckout.hidden =
          productOptionsCartEnabled || !confirmed || !isCurrentVariantAvailable() || gpxUploadPending;
      }

      if (confirmState) {
        if (confirmed) {
          confirmState.textContent = 'Kartenausschnitt bestätigt. Koordinaten, Maßstab und Ausrichtung sind gesperrt.';
          confirmState.dataset.state = 'confirmed';
        } else if (coordinatesLocked) {
          confirmState.textContent = 'Auswahl gesperrt. Bitte den Kartenausschnitt noch bestätigen.';
          delete confirmState.dataset.state;
        } else if (selection) {
          confirmState.textContent = 'Bitte den aktuellen Kartenausschnitt bestätigen.';
          delete confirmState.dataset.state;
        } else {
          confirmState.textContent = 'Die Karte wird vorbereitet.';
          delete confirmState.dataset.state;
        }
      }

      setSubmitBlocked(!confirmed || !coordinatesLocked);
    }

    function updateSelectionSummary(nextSelection) {
      if (!nextSelection) return;

      const hadSelection = Boolean(selection);
      selection = nextSelection;
      confirmed = false;
      if (hadSelection && isCoordinatesLocked()) {
        setCoordinatesLocked(false, false);
      }

      if (summaryCenter) {
        const longitude = formatNumber(nextSelection.center?.[0], 5);
        const latitude = formatNumber(nextSelection.center?.[1], 5);
        summaryCenter.textContent = `${latitude}, ${longitude}`;
      }
      if (summarySize) {
        summarySize.textContent = `${formatNumber(nextSelection.widthKm)} × ${formatNumber(nextSelection.heightKm)} km`;
      }
      if (summaryRotation) {
        summaryRotation.textContent =
          `${Math.round(Number(nextSelection.rotationDeg) || 0)}° im Uhrzeigersinn`;
      }

      clearError();
      updateConfirmationState();
    }

    function updateSnowSummary(nextSettings = {}) {
      if (typeof nextSettings.enabled === 'boolean') {
        snowSettings.enabled = nextSettings.enabled;
      }
      const nextSnowline = Number(nextSettings.baseSnowlineM);
      if (Number.isFinite(nextSnowline)) {
        snowSettings.baseSnowlineM = nextSnowline;
      }
      if (typeof nextSettings.available === 'boolean') {
        snowSettings.available = nextSettings.available;
      } else if (typeof nextSettings.enabled === 'boolean') {
        snowSettings.available = true;
      }

      const hasSnowline = snowSettings.available && Number.isFinite(snowSettings.baseSnowlineM);
      if (snowPropertyInput) {
        snowPropertyInput.value = snowSettings.enabled ? 'Ein' : 'Aus';
      }
      if (snowlinePropertyInput) {
        snowlinePropertyInput.value = hasSnowline
          ? String(Math.round(snowSettings.baseSnowlineM))
          : '';
      }
      root.dispatchEvent(new CustomEvent('bm:customMountain:snow-summary-change', {
        bubbles: true,
        detail: {
          enabled: snowSettings.enabled,
          snowlineM: hasSnowline ? Math.round(snowSettings.baseSnowlineM) : null
        }
      }));
    }

    function updateVariantSummary() {
      updateVariantExample();
      if (summaryVariant) {
        summaryVariant.textContent = readSelectedOptions(root, getVariantInput(), productData);
      }
      if (summaryQuantity && quantityInput) {
        summaryQuantity.textContent = quantityInput.value || '1';
      }
      if (dynamicCheckout && confirmed) {
        dynamicCheckout.hidden = productOptionsCartEnabled || !isCurrentVariantAvailable() || gpxUploadPending;
      }
    }

    function scheduleVariantSummaryUpdate() {
      if (variantFrameId !== null) window.cancelAnimationFrame(variantFrameId);
      if (variantTimeoutId !== null) window.clearTimeout(variantTimeoutId);

      variantFrameId = window.requestAnimationFrame(() => {
        variantFrameId = null;
        syncVariantSelectionToForm();
        updateVariantSummary();
        updateConfirmationState();
        variantTimeoutId = window.setTimeout(() => {
          variantTimeoutId = null;
          syncVariantSelectionToForm();
          updateVariantSummary();
          updateConfirmationState();
        }, VARIANT_SYNC_DELAY_MS);
      });
    }

    function confirmSelection() {
      if (!selection) {
        announceError('Bitte warte, bis der Kartenausschnitt vollständig geladen ist.');
        return;
      }

      if (confirmed && isCoordinatesLocked()) {
        if (!setCoordinatesLocked(false)) {
          announceError('Die Auswahl konnte nicht entsperrt werden. Bitte lade die Seite neu.');
          return;
        }
        confirmed = false;
        clearError();
        updateConfirmationState();
        confirmButton.focus({ preventScroll: true });
        return;
      }

      if (!isCoordinatesLocked() && !setCoordinatesLocked(true)) {
        announceError('Die Auswahl konnte nicht gesperrt werden. Bitte versuche es erneut.');
        return;
      }

      confirmed = true;
      clearError();
      updateConfirmationState();
      submitButton.focus({ preventScroll: true });
    }

    function validateBeforeSubmit(event) {
      if (confirmed && selection && isCoordinatesLocked()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      announceError(
        'Bitte bestätige zuerst den Kartenausschnitt. Deine Auswahl wurde noch nicht für die Bestellung freigegeben.'
      );
      confirmButton.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
    }

    function handleSelectionEvent(event) {
      if (event.target !== selectionRoot || event.detail?.sectionId !== selectionRoot.dataset.sectionId) return;
      updateSelectionSummary(event.detail);
    }

    function handleSelectionLockEvent(event) {
      if (event.target !== selectionRoot || event.detail?.sectionId !== selectionRoot.dataset.sectionId) return;
      if (event.detail?.locked === false) confirmed = false;
      updateConfirmationState();
    }

    function handleSnowSettingsEvent(event) {
      if (event.target !== selectionRoot || event.detail?.sectionId !== selectionRoot.dataset.sectionId) return;
      updateSnowSummary({
        available: true,
        enabled: event.detail?.enabled === true,
        baseSnowlineM: event.detail?.baseSnowlineM
      });
    }

    function handleGpxSettingsEvent(event) {
      if (event.target !== selectionRoot || event.detail?.sectionId !== selectionRoot.dataset.sectionId) return;
      gpxUploadPending = event.detail?.selected === true && event.detail?.uploaded !== true;
      if (event.detail?.uploaded === true) clearError();
      updateConfirmationState();
    }

    function handleProductInput(event) {
      if (!root.contains(event.target)) return;
      syncVariantSelectionToForm();
      scheduleVariantSummaryUpdate();
    }

    function handleFormVariantChange(event) {
      if (event.target !== getVariantInput()) return;
      scheduleVariantSummaryUpdate();
    }

    function enforceConfigurationGate() {
      if (
        disposed ||
        enforcingButtonState ||
        (confirmed && isCoordinatesLocked()) ||
        submitButton.disabled
      ) return;
      setSubmitBlocked(true);
    }

    confirmButton.addEventListener('click', confirmSelection);
    instructionMediaQuery.addEventListener('change', applyResponsiveInstructionState);
    variantExampleTrigger?.addEventListener('click', handleVariantExampleOpen);
    variantExampleClose?.addEventListener('click', closeVariantExample);
    variantExampleDialog?.addEventListener('click', handleVariantExampleBackdrop);
    variantExampleDialog?.addEventListener('touchstart', handleVariantExampleTouchStart, { passive: true });
    variantExampleDialog?.addEventListener('touchend', handleVariantExampleTouchEnd, { passive: true });
    variantExampleDialog?.addEventListener('touchcancel', handleVariantExampleTouchCancel, { passive: true });
    variantExampleDialog?.addEventListener('close', handleVariantExampleTouchCancel);
    selectionRoot.addEventListener(MAP_SELECTION_EVENT, handleSelectionEvent);
    selectionRoot.addEventListener(SELECTION_LOCK_EVENT, handleSelectionLockEvent);
    selectionRoot.addEventListener(SNOW_SETTINGS_EVENT, handleSnowSettingsEvent);
    selectionRoot.addEventListener(GPX_SETTINGS_EVENT, handleGpxSettingsEvent);
    root.addEventListener('change', handleProductInput);
    root.addEventListener('input', handleProductInput);
    form.addEventListener('change', handleFormVariantChange);
    form.addEventListener('submit', validateBeforeSubmit, true);

    const buttonObserver = new MutationObserver(enforceConfigurationGate);
    buttonObserver.observe(submitButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled']
    });

    syncVariantSelectionToForm();
    applyResponsiveInstructionState();
    updateVariantSummary();
    updateSnowSummary();
    updateConfirmationState();

    window.setTimeout(() => {
      if (disposed) return;
      const initialSelection = window.BM.bm_peakExplorer_getSelection?.(selectionRoot.dataset.sectionId);
      if (initialSelection) updateSelectionSummary(initialSelection);
      const initialGpx = window.BM.bm_peakExplorer_getGpxTrack?.(selectionRoot.dataset.sectionId);
      gpxUploadPending = initialGpx?.selected === true && initialGpx?.uploaded !== true;
      const currentSnowMode = String(selectionRoot.dataset.bmSnowMode || 'off');
      updateSnowSummary({
        available: Boolean(snowSlider),
        enabled: currentSnowMode !== 'off',
        baseSnowlineM: snowSlider?.value
      });
    }, 0);

    root.bmCustomMountainDestroy = () => {
      disposed = true;
      if (variantFrameId !== null) window.cancelAnimationFrame(variantFrameId);
      if (variantTimeoutId !== null) window.clearTimeout(variantTimeoutId);
      confirmButton.removeEventListener('click', confirmSelection);
      instructionMediaQuery.removeEventListener('change', applyResponsiveInstructionState);
      variantExampleTrigger?.removeEventListener('click', handleVariantExampleOpen);
      variantExampleClose?.removeEventListener('click', closeVariantExample);
      variantExampleDialog?.removeEventListener('click', handleVariantExampleBackdrop);
      variantExampleDialog?.removeEventListener('touchstart', handleVariantExampleTouchStart);
      variantExampleDialog?.removeEventListener('touchend', handleVariantExampleTouchEnd);
      variantExampleDialog?.removeEventListener('touchcancel', handleVariantExampleTouchCancel);
      variantExampleDialog?.removeEventListener('close', handleVariantExampleTouchCancel);
      closeVariantExample();
      selectionRoot.removeEventListener(MAP_SELECTION_EVENT, handleSelectionEvent);
      selectionRoot.removeEventListener(SELECTION_LOCK_EVENT, handleSelectionLockEvent);
      selectionRoot.removeEventListener(SNOW_SETTINGS_EVENT, handleSnowSettingsEvent);
      selectionRoot.removeEventListener(GPX_SETTINGS_EVENT, handleGpxSettingsEvent);
      root.removeEventListener('change', handleProductInput);
      root.removeEventListener('input', handleProductInput);
      form.removeEventListener('change', handleFormVariantChange);
      form.removeEventListener('submit', validateBeforeSubmit, true);
      buttonObserver.disconnect();
      if (keepMainMediaStable && originalUpdateMedia) {
        if (hadOwnUpdateMedia) root.updateMedia = originalUpdateMedia;
        else delete root.updateMedia;
      }
      delete root.dataset.bmInitialized;
      delete root.bmCustomMountainDestroy;
    };
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  let reinitializeFrameId = null;

  function reinitializeAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => root.bmCustomMountainDestroy?.());
    initAll(document);
  }

  function scheduleReinitializeAll() {
    if (reinitializeFrameId !== null) window.cancelAnimationFrame(reinitializeFrameId);
    reinitializeFrameId = window.requestAnimationFrame(() => {
      reinitializeFrameId = null;
      reinitializeAll();
    });
  }

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', scheduleReinitializeAll);
  document.addEventListener('shopify:section:unload', scheduleReinitializeAll);
})();
